import { useDebouncedValue } from 'hooks'
import { useMemo, useState } from 'react'
import { MAX_VISIBLE_TAGS, SEARCH_DEBOUNCE_MS } from '../constants'
import type { SanityArticleListItem, SanityHubWithArticles, SanityTag } from '../interfaces'

export interface ArticleSearchResult {
  article: SanityArticleListItem
  hub: SanityHubWithArticles
}

interface UseArticleFiltersResult {
  query: string
  setQuery: (value: string) => void
  selectedTags: string[]
  setSelectedTags: (value: string[]) => void
  isSearching: boolean
  // True while a query or tag filter is active: the UI swaps the hub tabs for
  // a flat cross-hub result list.
  showFlatResults: boolean
  allTags: SanityTag[]
  visibleTags: SanityTag[]
  overflowTags: SanityTag[]
  results: ArticleSearchResult[]
}

// Search + tag filtering across every hub, debounced. Pure client-side
// filtering over the already-fetched hub list, as in the prototype.
export const useArticleFilters = (hubs: SanityHubWithArticles[]): UseArticleFiltersResult => {
  const [query, setQuery] = useState('')
  const [debouncedQuery] = useDebouncedValue(query, SEARCH_DEBOUNCE_MS)
  const [selectedTags, setSelectedTags] = useState<string[]>([])

  const trimmedQuery = debouncedQuery.trim().toLowerCase()
  const isSearching = trimmedQuery.length > 0
  const isFilteringByTags = selectedTags.length > 0
  const showFlatResults = isSearching || isFilteringByTags

  // Every distinct tag used by at least one article, for the filter chips.
  const allTags = useMemo(() => {
    const byId = new Map<string, SanityTag>()
    hubs.forEach(hub => hub.articles.forEach(article => article.tags?.forEach(tag => byId.set(tag._id, tag))))
    return Array.from(byId.values()).sort((a, b) => a.title.localeCompare(b.title))
  }, [hubs])

  // Split tags between the inline row and the overflow popover. Selected tags
  // are always kept inline so an active filter is never hidden. The
  // alphabetical order is preserved so chips never jump under the cursor when
  // clicked; overflow selections are promoted into their alphabetical slot.
  const { visibleTags, overflowTags } = useMemo(() => {
    if (allTags.length <= MAX_VISIBLE_TAGS) {
      return { visibleTags: allTags, overflowTags: [] as SanityTag[] }
    }

    const inlineIds = new Set(allTags.slice(0, MAX_VISIBLE_TAGS).map(tag => tag._id))
    const selectedIds = new Set(selectedTags)
    const visible = allTags.filter(tag => inlineIds.has(tag._id) || selectedIds.has(tag._id))
    const visibleIds = new Set(visible.map(tag => tag._id))

    return {
      visibleTags: visible,
      overflowTags: allTags.filter(tag => !visibleIds.has(tag._id)),
    }
  }, [allTags, selectedTags])

  // Flat, filtered list across every hub, kept alongside its hub for the
  // badge. Active whenever a query or tag filter is set; both must match.
  const results = useMemo(() => {
    if (!showFlatResults) return []

    return hubs
      .flatMap(hub =>
        hub.articles
          .filter(article => {
            const matchesQuery =
              !isSearching ||
              article.title.toLowerCase().includes(trimmedQuery) ||
              (article.excerpt?.toLowerCase().includes(trimmedQuery) ?? false)
            const matchesTags =
              !isFilteringByTags || (article.tags?.some(tag => selectedTags.includes(tag._id)) ?? false)
            return matchesQuery && matchesTags
          })
          .map(article => ({ article, hub })),
      )
      .sort((a, b) => a.article.title.localeCompare(b.article.title))
  }, [hubs, showFlatResults, isSearching, trimmedQuery, isFilteringByTags, selectedTags])

  return {
    query,
    setQuery,
    selectedTags,
    setSelectedTags,
    isSearching,
    showFlatResults,
    allTags,
    visibleTags,
    overflowTags,
    results,
  }
}
